import React from 'react';
import cn from '@helpers/className';
import Link from 'next/link';
import { buttonVariants } from './ButtonVariants';

interface LinkButtonProps {
  variant?: keyof typeof buttonVariants;
  size?: 'lg' | 'md' | 'sm';
  fullWidth?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  href: string;
  id?: string;
  title?: string;
  openInNewTab?: boolean;
  onClick?: (
    event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>,
  ) => void;
}

/**
 * LinkButton Component Documentation
 *
 * A component that renders an anchor tag (`<a>`) styled as a button.
 * It provides a consistent look and feel for links that need to appear as buttons.
 *
 * Key features:
 * - Renders as an `<a>` tag, making it suitable for navigation.
 * - Offers predefined visual variants (primary, secondary, ultramarine).
 * - Supports different sizes (lg, md, sm).
 * - Can be set to full width.
 * - Handles disabled state.
 */

export default function LinkButton({
  variant = 'primary',
  size = 'md',
  fullWidth = true,
  disabled = false,
  children,
  className = '',
  href,
  id,
  title,
  openInNewTab = false,
  onClick,
}: LinkButtonProps) {
  // Base styles shared across all variants
  const baseStyles =
    'flex font-rp-basetica items-center justify-center py-2.5 rounded-solar-sm transition-colors duration-400 ease-custom';

  // Size-specific styles
  const sizeStyles: Record<string, string> = {
    lg: 'h-11 text-base px-spacing-md',
    md: 'h-8.5 text-sm px-spacing-md',
    sm: 'h-7 text-xs px-spacing-sm',
  };

  const classes = cn({
    [baseStyles]: true,
    [buttonVariants[variant].default]: !disabled,
    [buttonVariants[variant].hover]: !disabled,
    [buttonVariants[variant].pressed]: !disabled,
    [buttonVariants[variant].focus]: !disabled,
    [buttonVariants[variant].disabled]: disabled,
    [sizeStyles[size]]: true,
    'w-full': fullWidth,
    [className]: !!className,
  });

  return (
    <Link href={href} passHref>
      <a
        id={id}
        title={title}
        className={classes}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
          }
        }}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick?.(e);
          }
        }}
      >
        {children}
      </a>
    </Link>
  );
}
